import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useVerifyMagicLinkQuery, useSetPasswordMutation } from '../../store/vendorApi';
import { Lock, CheckCircle, XCircle, Loader, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

const SetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isResetMode = searchParams.get('mode') === 'reset';
  const { isAuthenticated } = useAuth();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordErrorState] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { data: verificationData, isLoading: isVerifying, error: verificationError } = useVerifyMagicLinkQuery(
    token || '',
    { skip: !token }
  );

  const [setPasswordMutation, { isLoading: isSettingPassword, error: setPasswordError }] = useSetPasswordMutation();

  useEffect(() => {
    if (!token) {
      setPasswordErrorState(t('auth.setPassword.noTokenProvidedError'));
    }
  }, [token, t]);

  useEffect(() => {
    if (isAuthenticated && token) {
      setSuccessMessage(t('auth.setPassword.alreadyConnectedMessage'));
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
    }
  }, [isAuthenticated, token, navigate, t]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordErrorState('');

    if (confirmPassword && newPassword !== confirmPassword) {
      setPasswordErrorState(t('auth.setPassword.passwordsDoNotMatch'));
    } else if (newPassword.length > 0 && newPassword.length < 6) {
      setPasswordErrorState(t('auth.setPassword.passwordTooShort'));
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    setPasswordErrorState('');

    if (password !== newConfirmPassword) {
      setPasswordErrorState(t('auth.setPassword.passwordsDoNotMatch'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordErrorState('');
    setSuccessMessage('');

    if (!password || !confirmPassword) {
      setPasswordErrorState(t('auth.setPassword.fillAllFields'));
      return;
    }

    if (password.length < 6) {
      setPasswordErrorState(t('auth.setPassword.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setPasswordErrorState(t('auth.setPassword.passwordsDoNotMatch'));
      return;
    }

    if (!token) {
      setPasswordErrorState(t('auth.setPassword.invalidToken'));
      return;
    }

    try {
      const result = await setPasswordMutation({ token, password }).unwrap();
      setSuccessMessage(
        isResetMode
          ? t('auth.setPassword.passwordResetSuccess')
          : t('auth.setPassword.passwordSetSuccess')
      );
      setTimeout(() => {
        navigate('/login', {
          state: {
            email: result.email,
            message: isResetMode
              ? t('auth.setPassword.loginWithNewPasswordReset')
              : t('auth.setPassword.loginWithNewPasswordSet'),
          },
        });
      }, 2000);
    } catch (err: any) {
      setPasswordErrorState(err.data?.message || t('auth.setPassword.setPasswordFailed'));
    }
  };

  if (isAuthenticated && token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.setPassword.alreadyConnected')}</h1>
            <p className="text-gray-600 mb-6">{t('auth.setPassword.alreadyConnectedMessage')}</p>
            <Link to="/dashboard" className="btn-primary inline-flex items-center">
              {t('auth.setPassword.goToDashboard')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.setPassword.invalidLink')}</h1>
            <p className="text-gray-600 mb-6">{t('auth.setPassword.noTokenProvided')}</p>
            <Link to="/login" className="btn-primary inline-flex items-center">
              {t('auth.setPassword.goToLogin')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="text-center">
            <Loader className="w-16 h-16 text-purple-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.setPassword.verifyingLink')}</h1>
            <p className="text-gray-600">{t('auth.setPassword.verifyingMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (verificationError || !verificationData?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.setPassword.invalidOrExpired')}</h1>
            <p className="text-gray-600 mb-6">
              {verificationData?.message || t('auth.setPassword.invalidOrExpiredMessage')}
            </p>
            <Link to="/login" className="btn-primary inline-flex items-center">
              {t('auth.setPassword.goToLogin')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.setPassword.successTitle')}</h1>
            <p className="text-gray-600">{successMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isResetMode
              ? t('auth.setPassword.resetTitle')
              : t('auth.setPassword.welcomeTitle', { name: verificationData.firstName ?? t('auth.setPassword.welcomeThere') })}
          </h1>
          <p className="text-gray-600 mt-2">
            {isResetMode ? t('auth.setPassword.resetSubtitle') : t('auth.setPassword.setupSubtitle')}
          </p>
          {verificationData.email && (
            <p className="text-sm text-gray-400 mt-1">{verificationData.email}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.setPassword.newPassword')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handlePasswordChange}
                className="form-input pr-12"
                placeholder={t('auth.setPassword.enterPassword')}
                required
                minLength={6}
                disabled={isSettingPassword}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? t('auth.setPassword.hidePassword') : t('auth.setPassword.showPassword')}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('auth.setPassword.minCharacters')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.setPassword.confirmPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className="form-input pr-12"
                placeholder={t('auth.setPassword.confirmPasswordPlaceholder')}
                required
                minLength={6}
                disabled={isSettingPassword}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showConfirmPassword ? t('auth.setPassword.hidePassword') : t('auth.setPassword.showPassword')}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {(passwordError || setPasswordError) && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {passwordError || (setPasswordError && 'data' in setPasswordError && typeof setPasswordError.data === 'object' && setPasswordError.data !== null && 'message' in setPasswordError.data
                ? (setPasswordError.data as { message: string }).message
                : t('auth.setPassword.setPasswordGenericFailure'))}
            </div>
          )}

          <button
            type="submit"
            disabled={isSettingPassword || !!passwordError}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSettingPassword ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                {t('auth.setPassword.settingPassword')}
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                {isResetMode ? t('auth.setPassword.resetPassword') : t('auth.setPassword.setPassword')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-purple-600 hover:text-purple-800 font-medium transition-colors text-sm"
          >
            {t('auth.setPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;

